<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, watch } from "vue"
import {
    SourceLegacyChannel,
    SourceModel,
    SourceModelParameterDetail,
} from "@types"
import { useConfigStore } from "@core/stores/config.store"

// ================================
// TYPES & INTERFACES
// ================================

export type SourceLegacyChannelWithDisabled = SourceLegacyChannel & {
    disabled?: boolean
}

interface VariableParameters {
    [variableType: string]: SourceModelParameterDetail[]
}

interface Props {
    /**
     * Identifiant de l'opérateur
     */
    operatorId: string | null
    /**
     * Identifiant du modèle de source sélectionné
     */
    modelId: string | null
    /**
     * Liste des channels (activés ou désactivés) - toujours de la taille du nombre de channels du modèle
     */
    modelValue: SourceLegacyChannelWithDisabled[]
}

interface Emits {
    /**
     * Émis lors de la mise à jour des channels (toujours de la taille du nombre de channels du modèle)
     */
    (e: "update:modelValue", value: SourceLegacyChannelWithDisabled[]): void
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    operatorId: null,
    modelId: null,
    modelValue: () => [],
})

const emit = defineEmits<Emits>()

// ================================
// COMPOSABLES
// ================================
const configStore = useConfigStore()

// ================================
// COMPUTED
// ================================

/**
 * Récupère le modèle depuis le cache operatorModelsCache en utilisant operatorId et modelId
 */
const model = computed((): SourceModel | null => {
    if (!props.operatorId || !props.modelId) return null

    const operatorModels = configStore.operatorModelsCache[props.operatorId]
    if (!operatorModels?.models) return null

    return operatorModels.models.find((m) => m.idname === props.modelId) || null
})

/**
 * Channels du modèle (helper pour éviter la répétition)
 */
const modelChannels = computed(() => model.value?.channels || [])

/**
 * Nombre de channels disponibles
 */
const amount = computed(() => modelChannels.value.length)

/**
 * Normalise modelValue pour avoir toujours exactement `amount` channels
 * Complète avec des channels désactivés si nécessaire, tronque si trop long
 * Initialise automatiquement les paramètres par défaut pour les channels qui en ont besoin
 */
const normalizedChannels = computed(() => {
    const channels = [...props.modelValue]

    // Complète avec des channels désactivés si nécessaire
    while (channels.length < amount.value) {
        channels.push({ type: "", disabled: true })
    }

    // Tronque si trop long et s'assure que disabled est toujours défini
    // Initialise les paramètres par défaut si nécessaire
    return channels.slice(0, amount.value).map((ch) => {
        const { disabled, ...rest } = ch
        const normalized: SourceLegacyChannelWithDisabled = {
            ...rest,
            disabled: disabled ?? false,
        }
        // Initialise les paramètres par défaut si le channel a un type
        return initializeChannelParameters(normalized, normalized.type)
    })
})

/**
 * Récupère les channels à afficher (tous jusqu'à amount, même désactivés)
 */
const getChannelsToDisplay = computed(() => {
    return modelChannels.value.map((channel, index) => ({
        color: channel.color,
        description: channel.description,
        variables: channel.variables,
        invalid: isChannelInvalid(index),
    }))
})

/**
 * Récupère la liste des variables disponibles pour le modèle sélectionné
 */
const getVariablesAvailables = computed((): VariableParameters => {
    if (!model.value?.variables) return {}

    return model.value.variables.reduce<VariableParameters>((acc, v) => {
        if (v.type) {
            acc[v.type] =
                v.parameters
                    ?.filter((p) => p.enabled && p._id_sourceparameter)
                    .map((p) => {
                        return { ...p._id_sourceparameter! }
                    }) || []
        }
        return acc
    }, {})
})

/**
 * Détecte les channels avec des doublons de type (uniquement parmi les non désactivés)
 */
const hasDuplicateChannels = computed(() => {
    const typeToIndices = new Map<string, number[]>()

    normalizedChannels.value.forEach((channel, index) => {
        if (!channel.disabled && channel.type) {
            const indices = typeToIndices.get(channel.type) || []
            indices.push(index)
            typeToIndices.set(channel.type, indices)
        }
    })

    return Array.from(typeToIndices.values())
        .filter((indices) => indices.length > 1)
        .flat()
})

/**
 * Vérifie si un channel a un doublon
 */
const isChannelInvalid = (index: number): boolean =>
    hasDuplicateChannels.value.includes(index)

// ================================
// UTILITY FUNCTIONS
// ================================

/**
 * Initialise les paramètres par défaut d'un channel si nécessaire
 * @param channel - Le channel à initialiser
 * @param variableType - Le type de variable du channel
 * @returns Le channel avec les paramètres initialisés
 */
function initializeChannelParameters(
    channel: SourceLegacyChannelWithDisabled,
    variableType: string | null | undefined
): SourceLegacyChannelWithDisabled {
    if (!variableType || channel.disabled) {
        return channel
    }

    const params = getVariablesAvailables.value[variableType]
    if (!params || params.length === 0) {
        return channel
    }

    const updated: SourceLegacyChannelWithDisabled = { ...channel }

    // Initialise les paramètres de type "converter"
    const converterParams = params.filter((p) => p.type === "converter")
    if (converterParams.length > 0 && !updated.converter) {
        const converterParam = converterParams[0] // Prend le premier converter
        updated.converter = {
            name: converterParam.idname,
            a: converterParam.default,
            b: 0,
        }
    }

    // Initialise les paramètres de type "transformer"
    const transformerParams = params.filter(
        (p) => p.type === "transformer" && p.to
    )
    if (transformerParams.length > 0) {
        // Initialise uniquement les transformers qui ne sont pas déjà présents
        const existingTransformers = updated.transformer || []
        const missingTransformers = transformerParams.filter(
            (param) => !existingTransformers.some((t) => t.to === param.to)
        )

        if (missingTransformers.length > 0) {
            updated.transformer = [
                ...existingTransformers,
                ...missingTransformers.map((param) => ({
                    to: param.to!,
                    a: param.default,
                    b: 0,
                })),
            ]
        }
    }

    return updated
}

/**
 * Gère la mise à jour d'un channel
 */
function onChannelUpdate(
    index: number,
    channel: SourceLegacyChannelWithDisabled
): void {
    const updated = [...normalizedChannels.value]
    // Normalise le channel pour s'assurer que disabled est toujours défini
    // et initialise les paramètres par défaut si nécessaire
    const normalizedChannel = {
        ...channel,
        disabled: channel.disabled ?? false,
    }
    const initializedChannel = initializeChannelParameters(
        normalizedChannel,
        normalizedChannel.type
    )
    updated[index] = initializedChannel
    emit("update:modelValue", updated)
}

// ================================
// WATCHERS
// ================================

/**
 * Initialise les channels quand le modèle change
 * Si modelValue est incomplet, complète automatiquement
 * Initialise aussi les paramètres par défaut pour tous les channels
 */
watch(
    () => model.value,
    (newModel) => {
        if (!newModel?.channels?.length) {
            emit("update:modelValue", [])
            return
        }

        // Normalise les channels (qui initialise automatiquement les paramètres)
        const normalized = normalizedChannels.value

        // Compare les channels normalisés avec ceux reçus en props
        // pour détecter si des paramètres doivent être initialisés
        const needsUpdate =
            normalized.length !== props.modelValue.length ||
            normalized.some((ch, index) => {
                const original = props.modelValue[index]
                if (!original) return true

                // Compare les paramètres converter
                const hasConverter = !!ch.converter
                const originalHasConverter = !!original.converter
                if (hasConverter !== originalHasConverter) return true
                if (
                    hasConverter &&
                    originalHasConverter &&
                    (ch.converter?.a !== original.converter?.a ||
                        ch.converter?.name !== original.converter?.name)
                ) {
                    return true
                }

                // Compare les paramètres transformer
                const hasTransformer =
                    !!ch.transformer && ch.transformer.length > 0
                const originalHasTransformer =
                    !!original.transformer && original.transformer.length > 0
                if (hasTransformer !== originalHasTransformer) return true
                if (hasTransformer && originalHasTransformer) {
                    // Compare chaque transformer
                    const chTransformers = ch.transformer || []
                    const origTransformers = original.transformer || []
                    if (chTransformers.length !== origTransformers.length)
                        return true
                    for (const t of chTransformers) {
                        const origT = origTransformers.find(
                            (ot) => ot.to === t.to
                        )
                        if (!origT || origT.a !== t.a) return true
                    }
                }

                return false
            })

        // Si des changements sont détectés, émet la version normalisée
        if (needsUpdate) {
            emit("update:modelValue", normalized)
        }
    },
    { immediate: true }
)

defineExpose({
    isValid: () => hasDuplicateChannels.value.length === 0,
})
</script>

<template>
    <div class="flex flex-col gap-4">
        <SensorFormChannel
            v-for="(channel, index) in getChannelsToDisplay"
            :key="`channel-${index}`"
            :id="`channel-${index}`"
            :index="index"
            :color="channel.color"
            :description="channel.description"
            :variable-parameters="getVariablesAvailables"
            :invalid="channel.invalid"
            :model-value="normalizedChannels[index]"
            @update:model-value="
                (value: SourceLegacyChannelWithDisabled) =>
                    onChannelUpdate(index, value)
            "
        />
    </div>
</template>
